import type { DREHierarchicalLine, DREHierarchicalNode, DRELineType, DREParentPath } from "../../../types/dashboard";
import type { ExpandNodeParams } from "./index";
import { useT } from "@hooks";
import { toPtBrNumberFormat } from "@utils/format";

type DRERowItem = DREHierarchicalLine | DREHierarchicalNode;

const isNode = (item: DRERowItem): item is DREHierarchicalNode => {
  return "nodeType" in item;
};

const extractOriginalId = (compositeId: string): string => {
  const parts = compositeId.split("_");
  return parts[0];
};

const buildParentPath = (item: DREHierarchicalNode, parentPath?: DREParentPath): DREParentPath => {
  const newPath: DREParentPath = { ...parentPath };
  const originalId = extractOriginalId(item.id);

  switch (item.nodeType) {
    case "FILIAL":
      newPath.branchId = Number.parseInt(originalId, 10) || undefined;
      break;
    case "SEGMENTO":
      newPath.segmentId = Number.parseInt(originalId, 10) || undefined;
      break;
    case "CLIENTE":
    case "FORNECEDOR":
      newPath.clientOrSupplierId = Number.parseInt(originalId, 10) || undefined;
      break;
    case "VIAGEM":
      newPath.tripCode = originalId;
      break;
    case "VEICULO":
      newPath.vehicleId = originalId;
      break;
  }

  return newPath;
};

const hasLoadedChildren = (item: DRERowItem): boolean => Boolean(item.children?.length);

const canFetchNodeChildren = (item: DRERowItem): boolean => isNode(item) && item.expandable && !hasLoadedChildren(item);

const isBoldLine = (lineType: DRELineType): boolean => lineType === "LUCRO_OPERACIONAL" || lineType === "MARGEM_LUCRO";

const buildDiffLabel = (isPercentageLine: boolean, isNegative: boolean): string => {
  if (isPercentageLine) {
    return "";
  }
  return isNegative ? "- R$ 0,00" : "R$ 0,00";
};

const buildRowClassName = (isLast: boolean): string =>
  `display-flex align-items-center justify-content-between gap-10 padding-y-10 border ${
    isLast ? "border-none" : "border-bottom-only"
  }`;

const buildValueCellClassName = (isNegative: boolean, isBold: boolean): string =>
  `flex-0 width-100 width-150-sm text-right ${isNegative ? "text-color-danger" : ""} ${isBold ? "bold" : ""}`;

export type DREIndent = {
  base: number;
  step: number;
  maxLevel: number;
};

interface DRERowProps {
  item: DRERowItem;
  lineType: DRELineType;
  expandedItems: string[];
  loadingNodes: string[];
  onToggle: (id: string) => void;
  onExpandNode: (params: ExpandNodeParams) => Promise<void>;
  indent: DREIndent;
  parentPath?: DREParentPath;
  isLast?: boolean;
  depth?: number;
}

export const getIndentStyle = (level: number, indent: DREIndent): React.CSSProperties => {
  const effectiveLevel = Math.min(level, indent.maxLevel);
  const paddingLeft = indent.base + effectiveLevel * indent.step;
  return { paddingLeft: `${paddingLeft}px`, minWidth: 0 };
};

export const DRE_TOGGLE_COLUMN_STYLE: React.CSSProperties = { width: 25, flex: "0 0 auto" };

export const DRE_ITEM_LABEL_STYLE: React.CSSProperties = { minWidth: 0 };

const DRE_TOGGLE_BUTTON_STYLE: React.CSSProperties = {
  background: "none",
  border: 0,
  padding: 0,
  font: "inherit",
  lineHeight: "inherit",
  color: "inherit",
  cursor: "pointer",
  ...DRE_TOGGLE_COLUMN_STYLE,
};

interface DREToggleProps {
  isLoading: boolean;
  isExpanded: boolean;
  onActivate: () => void;
}

function DREToggle({ isLoading, isExpanded, onActivate }: Readonly<DREToggleProps>) {
  const handleKeyDown = (evento: React.KeyboardEvent<HTMLButtonElement>) => {
    if (evento.key === "Enter" || evento.key === " ") {
      evento.preventDefault();
      onActivate();
    }
  };

  return (
    <button
      type="button"
      className={`cursor-pointer text-center ${isLoading ? "opacity-50" : ""}`}
      style={DRE_TOGGLE_BUTTON_STYLE}
      tabIndex={0}
      onClick={isLoading ? undefined : onActivate}
      onKeyDown={isLoading ? undefined : handleKeyDown}
    >
      {isLoading ? (
        <span className="rioglyph rioglyph-spinner spinning" />
      ) : (
        <span className={`rioglyph ${isExpanded ? "rioglyph-chevron-up" : "rioglyph-chevron-down"}`} />
      )}
    </button>
  );
}

export default function DRERow({
  item,
  lineType,
  expandedItems,
  loadingNodes,
  onToggle,
  onExpandNode,
  indent,
  parentPath,
  isLast = false,
  depth = 0,
}: Readonly<DRERowProps>) {
  const t = useT();
  const hasChildren = hasLoadedChildren(item);
  const isExpanded = expandedItems.includes(item.id);
  const isLoading = loadingNodes.includes(item.id);
  const indentStyle = getIndentStyle(depth, indent);
  const isBold = isBoldLine(lineType);
  const displayLabel = isNode(item) ? item.label : t(`dre.line.${lineType}`);
  const isNegative = item.value < 0;
  const canFetchChildren = canFetchNodeChildren(item);
  // O backend ainda não expõe a quebra Previsto x Realizado por linha do DRE.
  // Enquanto isso, assume-se que tudo já lançado está realizado (Previsto = Realizado).
  const diffLabel = buildDiffLabel(lineType === "MARGEM_LUCRO", isNegative);
  const displayValue = toPtBrNumberFormat(item.formattedValue);
  const valueCellClassName = buildValueCellClassName(isNegative, isBold);
  const showToggle = hasChildren || canFetchChildren;

  const handleToggle = async () => {
    if (isExpanded) {
      onToggle(item.id);
      return;
    }

    if (canFetchChildren && isNode(item)) {
      const originalId = extractOriginalId(item.id);
      await onExpandNode({
        nodeId: originalId,
        compositeId: item.id,
        nodeType: item.nodeType,
        lineType,
        parentPath,
      });
    }

    onToggle(item.id);
  };

  const childParentPath = isNode(item) ? buildParentPath(item, parentPath) : parentPath;

  return (
    <>
      <div key={item.id} className={buildRowClassName(isLast)}>
        <div className="flex-1-1 min-width-0 display-flex align-items-center gap-5" style={indentStyle}>
          {showToggle ? (
            <DREToggle isLoading={isLoading} isExpanded={isExpanded} onActivate={handleToggle} />
          ) : (
            <span style={DRE_TOGGLE_COLUMN_STYLE} />
          )}
          <span className={isBold ? "bold" : ""} style={DRE_ITEM_LABEL_STYLE}>
            {displayLabel}
          </span>
        </div>

        <div className={valueCellClassName}>{displayValue}</div>

        <div className={valueCellClassName}>{displayValue}</div>

        <div className={valueCellClassName}>{diffLabel}</div>
      </div>

      {hasChildren &&
        isExpanded &&
        item.children!.map((child, index, array) => (
          <DRERow
            item={child}
            key={child.id}
            lineType={lineType}
            onToggle={onToggle}
            expandedItems={expandedItems}
            loadingNodes={loadingNodes}
            onExpandNode={onExpandNode}
            indent={indent}
            parentPath={childParentPath}
            isLast={isLast && index === array.length - 1}
            depth={depth + 1}
          />
        ))}
    </>
  );
}
